import { RootState } from 'client/_redux/reducers';
import {
  getTourDashboardById,
  updateTourDashboard,
  deleteTourDashboard,
  addStepToTourDashboard,
  updateStepInTourDashboard,
  deleteStepFromTourDashboard,
} from 'dashboard/_redux/actions/tours';
import { ITourFormValues, ITourStep } from 'client/models/ITour';
import { ICategoryTour } from 'client/models/ICategoryTour';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { clientApi } from 'api';

export const useTourDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [t] = useTranslation();
  const history = useNavigate();
  const selectedTour = useSelector(
    (state: RootState) => state.dashboardTours.selectedTour,
  );
  const dispatch = useDispatch();

  // État pour les catégories
  const [categories, setCategories] = useState<ICategoryTour[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Charger les catégories au montage
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        console.log('🔄 Chargement des catégories...');
        const response = await clientApi.get('category-tours', {
          params: { limit: 100 },
        });
        console.log('✅ Réponse API catégories:', response.data);
        const categories = response.data.categoryTours || [];
        console.log('📋 Catégories chargées:', categories);
        setCategories(categories);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des catégories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // État pour le formulaire principal du tour
  const [formData, setFormData] = useState<Partial<ITourFormValues>>({
    name: '',
    description: '',
    categoryTour: '',
    difficulty: 'medium',
    status: 'draft',
    estimatedDuration: 0,
    distance: 0,
    price: 0,
    savings: 0,
    tags: [],
  });

  // État pour la gestion des étapes
  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<ITourStep | null>(null);
  const [stepFormData, setStepFormData] = useState<Partial<ITourStep>>({
    title: '',
    description: '',
    duration: 0,
    location: {
      address: '',
      latitude: undefined,
      longitude: undefined,
    },
    photos: [],
    videoUrl: '',
    tips: '',
  });

  // Charger le tour
  useEffect(() => {
    if (!id || typeof id !== 'string') return;
    dispatch(getTourDashboardById(id));
  }, [dispatch, id]);

  // Mettre à jour le formulaire quand le tour est chargé
  useEffect(() => {
    if (selectedTour) {
      setFormData({
        name: selectedTour.name,
        name_en: selectedTour.name_en || '',
        description: selectedTour.description,
        description_en: selectedTour.description_en || '',
        categoryTour: typeof selectedTour.categoryTour === 'object'
          ? selectedTour.categoryTour._id
          : selectedTour.categoryTour,
        difficulty: selectedTour.difficulty,
        status: selectedTour.status,
        estimatedDuration: selectedTour.estimatedDuration,
        distance: selectedTour.distance,
        price: selectedTour.price,
        savings: selectedTour.savings,
        tags: selectedTour.tags,
      });
    }
  }, [selectedTour]);

  // Gestion du formulaire principal
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'estimatedDuration' || name === 'distance' || name === 'price'
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(updateTourDashboard(id, formData));
  };

  const handleDelete = () => {
    if (window.confirm(t('dashboard.tourPage.confirmDelete') || 'Êtes-vous sûr de vouloir supprimer ce parcours ?')) {
      dispatch(
        deleteTourDashboard(id, () => {
          history('/dashboard/tours');
        }),
      );
    }
  };

  // Gestion des étapes
  const handleOpenStepModal = (step?: ITourStep) => {
    if (step) {
      setEditingStep(step);
      setStepFormData(step);
    } else {
      setEditingStep(null);
      setStepFormData({
        title: '',
        description: '',
        duration: 0,
        location: {
          address: '',
          latitude: undefined,
          longitude: undefined,
        },
        photos: [],
        videoUrl: '',
        tips: '',
      });
    }
    setIsStepModalOpen(true);
  };

  const handleCloseStepModal = () => {
    setIsStepModalOpen(false);
    setEditingStep(null);
  };

  const handleStepInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setStepFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: locationField === 'latitude' || locationField === 'longitude'
            ? parseFloat(value) || undefined
            : value,
        },
      }));
    } else {
      setStepFormData((prev) => ({
        ...prev,
        [name]: name === 'duration' ? parseInt(value) || 0 : value,
      }));
    }
  };

  const handleSubmitStep = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingStep) {
      // Mise à jour d'une étape existante
      dispatch(
        updateStepInTourDashboard(id, editingStep.order, stepFormData, () => {
          handleCloseStepModal();
          dispatch(getTourDashboardById(id)); // Recharger le tour
        }),
      );
    } else {
      // Ajout d'une nouvelle étape
      dispatch(
        addStepToTourDashboard(id, stepFormData as Omit<ITourStep, 'order'>, () => {
          handleCloseStepModal();
          dispatch(getTourDashboardById(id)); // Recharger le tour
        }),
      );
    }
  };

  const handleDeleteStep = (stepOrder: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette étape ?')) {
      dispatch(
        deleteStepFromTourDashboard(id, stepOrder, () => {
          dispatch(getTourDashboardById(id)); // Recharger le tour
        }),
      );
    }
  };

  // Gestion de la sélection de localisation avec Google Maps
  const handleLocationSelect = (location: {
    address: string;
    latitude: number;
    longitude: number;
  }) => {
    setStepFormData((prev) => ({
      ...prev,
      location: {
        address: location.address,
        latitude: location.latitude,
        longitude: location.longitude,
      },
    }));
  };

  return {
    selectedTour,
    formData,
    handleInputChange,
    handleSubmit,
    handleDelete,
    // Gestion des étapes
    isStepModalOpen,
    editingStep,
    stepFormData,
    handleOpenStepModal,
    handleCloseStepModal,
    handleStepInputChange,
    handleSubmitStep,
    handleDeleteStep,
    handleLocationSelect,
    // Catégories
    categories,
    loadingCategories,
    t,
  };
};
